import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { GetPitchByIdResponse, PitchFormData } from "../Models/PitchFormData";
import Select from "react-select";
import YesNoDialog from "../common/YesNoDialog";
import { PitchTeamData } from "../Models/PitchTeamData";
import { DialogType } from "../Models/DialogType";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSavePitch } from "../../api/mutations/Pitch/useSavePitch";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { useAppContext } from "../../Context/AppContext";
import { useDeletePitch } from "../../api/mutations/Pitch/useDeletePitch";
import { Button } from "react-bootstrap";
import { BsFloppy } from "react-icons/bs";
import { countries } from "countries-list";
import { useGetPitchesByUser } from "../../api/queries/Pitch/useGetPitchesByUser";
import { sectors } from "../MuslimInvest/Sectors";
import { useGetCompanyDetails } from "../../api/queries/MuslimInvest/useGetCompanyDetails";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";

const AllPitches = lazy(() => import("./AllPitches"));
const UploadFiles = lazy(() => import("./UploadFiles"));

const DEFAULT_FORM_DATA: PitchFormData = {
  id: 0,
  status: "Draft",
  name: "",
  companyName: "",
  companyRegistrationNumber: "",
  countryOfRegistration: "",
  companyDescription: "",
  mission: "",
  vision: "",
  sector: "",
  investmentType: "",
  investmentTicketSize: "",
  region: "",
  identifiedMarketProblem: "",
  solution: "",
  marketSize: "",
  growthPotential: "",
  targetAudience: "",
  revenueStreams: "",
  pricingStrategy: "",
  revenueForecast: "",
  profitMargins: "",
  breakEvenMargins: "",
  amountSought: "" as unknown as number,
  useOfFunds: "",
  teamMembers: [] as PitchTeamData[],
  files: [],
  pitchFiles: [],
};

const COUNTRY_OPTIONS = Object.entries(countries)
  .map(([_, country]) => ({ value: country.name, label: country.name }))
  .sort((a, b) => a.label.localeCompare(b.label));

const SECTOR_OPTIONS = [...sectors].sort().map((s) => ({ value: s, label: s }));

const INVESTMENT_SIZES = [
  { value: "£1k-10k", label: "£1k-£10k" },
  { value: "£10k-£50k", label: "£10k-£50k" },
  { value: "£50k-£100k", label: "£50k-£100k" },
  { value: "£100k-£500k", label: "£100k-£500k" },
  { value: "£500k-£1M", label: "£500k-£1M" },
  { value: "£1M-£5M", label: "£1M-£5M" },
  { value: "£5M+", label: "£5M+" },
];

const VALIDATION_MESSAGE_STYLE: React.CSSProperties = { fontSize: "0.875rem" };

function normalizeSectors(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value as string[];
  if (typeof value === "string") {
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function CreatePitch() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("create");
  const { mutateAsync: savePitchMutation, isPending } = useSavePitch();

  const [formData, setFormData] = useState<PitchFormData>(DEFAULT_FORM_DATA);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number>(-1);
  const [dialogMessage, setDialogMessage] = useState<string>("");
  const [dialogType, setDialogType] = useState<string>("");
  const [pitchStep, setPitchStep] = useState<number>(0);
  const { user: userData, objectId } = useAppContext();

  const queryClient = useQueryClient();

  const { data: pitchesData, refetch: fetchPitches } = useGetPitchesByUser({
    id: objectId || "",
    enabled: !!objectId,
  });

  const { data: companyData } = useGetCompanyDetails({
    userId: userData?.user.id.toString() || "",
  });

  const companyProfile = companyData ? companyData[0] : null;
  const pitch = useMemo(
    () => pitchesData?.pitches[0]?.pitch ?? null,
    [pitchesData]
  );

  useEffect(() => {
    if (userData?.user.isAdmin === 1) {
      localStorage.setItem("isAdmin", "true");
    }
  }, [userData?.user?.isAdmin]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors: pitchErrors },
  } = useForm<PitchFormData>({
    defaultValues: pitch ?? { status: "Draft" },
  });

  useEffect(() => {
    if (pitch) {
      reset(pitch);
    } else {
      reset({ status: "Draft" });
    }
  }, [pitch, reset]);

  const { mutate: deletePitch } = useDeletePitch({
    onSuccess: () => {
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ["user-pitches"] });
      fetchPitches();
      toast.success("Pitch deleted successfully");
      reset({ status: "Draft" });
    },
  });

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
  }, []);

  const submitPitch = async (data: PitchFormData) => {
    const formDataToSend = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      // ✅ Only send id if greater than 0
      if (key === "id" && typeof value === "number" && value <= 0) {
        return; // skip adding id
      }

      if (key === "files" && Array.isArray(value)) {
        value.forEach((file) => {
          if (file instanceof File) {
            formDataToSend.append("files", file);
          }
        });
      } else if (key === "teamMembers" && Array.isArray(value)) {
        formDataToSend.append("teamMembers", JSON.stringify(value));
      } else if (typeof value === "number" || typeof value === "boolean") {
        formDataToSend.append(key, value.toString());
      } else if (typeof value === "string") {
        formDataToSend.append(key, value);
      } else if (value !== undefined && value !== null) {
        formDataToSend.append(key, JSON.stringify(value));
      } else {
        formDataToSend.append(key, "");
      }
    });

    if (objectId !== null) {
      formDataToSend.append("CreatedByObjectId", objectId);
      formDataToSend.append("UserId", userData?.user.id.toString() || "");
    }

    if (companyProfile?.id !== null) {
      formDataToSend.append("CompanyId", companyProfile?.id.toString() || "");
    }
    const responsePitch = await savePitchMutation(formDataToSend);
    // ✅ Optional: fetch team members after pitch creation/update

    const responseTeamMembers = await axiosInstance.get(
      getAPIURL(`/Pitch/team-members-by-pitch-id/${responsePitch.data.id}`)
    );
    const teamMembers = responseTeamMembers.data;

    // ✅ Update local state
    setFormData((prevData) => ({
      ...prevData,
      ...responsePitch.data,
      ...teamMembers,
    }));
    setPitchStep(1);
  };

  const handleEditPitch = useCallback(
    async (id: number) => {
      try {
        setLoading(true);

        // await getPitchFiles(id);

        const responsePitch = await axiosInstance.get<GetPitchByIdResponse>(
          getAPIURL(`/Pitch/get-pitch-by-id/${id}?id=${id}`)
        );

        // 2️⃣ Get team members
        const responseTeamMembers = await axiosInstance.get(
          getAPIURL(`/Pitch/team-members-by-pitch-id/${id}`)
        );

        const teamMembers = responseTeamMembers.data;

        setFormData((prevData) => ({
          ...prevData,
          ...responsePitch.data.pitchViewModel.pitch,
          ...teamMembers,
        }));
        setPitchStep(0);
        reset(responsePitch.data.pitchViewModel.pitch ?? { status: "Draft" });
        setActiveTab("create");
      } catch (error) {
        toast.error("Error fetching Pitch. Please try again later");
      } finally {
        setLoading(false);
      }
    },
    [reset]
  );

  const handleDeletePitch = useCallback((pitch: PitchFormData) => {
    setIdToDelete(pitch.id);
    setDialogType(DialogType.DeletePitch.toString());
    setDialogMessage("Are you sure you want to delete " + pitch.name + " ?");
    setDialogVisible(true);
  }, []);

  const handleDialogClose = useCallback(
    (result: boolean) => {
      if (result) {
        switch (dialogType) {
          case "DeletePitch":
            deletePitch(idToDelete);
            break;
          default:
            break;
        }
      }
      setDialogVisible(false);
    },
    [dialogType, idToDelete, deletePitch]
  );

  const disableInput = useCallback(() => {
    return formData.status === "Submitted" || formData.status === "Accepted";
  }, [formData.status]);

  const pitchesList = useMemo(
    () => pitchesData?.pitches || [],
    [pitchesData]
  );

  const handleTabCreate = useCallback(() => {
    handleTabChange("create");
    setFormData(DEFAULT_FORM_DATA);
  }, [handleTabChange]);

  const handleTabView = useCallback(() => {
    handleTabChange("view");
    fetchPitches();
    setPitchStep(0);
  }, [handleTabChange, fetchPitches]);

  return (
    <div className="">
      <YesNoDialog
        title="Confirmation"
        message={dialogMessage}
        show={dialogVisible}
        onClose={handleDialogClose}
      />
      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}
      <div className="container mt-2 mb-3">
        <div className="nav nav-tabs mb-3">
          <button
            className={`nav-link ${
              activeTab === "create" ? "active text-primary" : "text-dark"
            }`}
            onClick={handleTabCreate}
          >
            Create New Pitch
          </button>
          <button
            className={`nav-link ${
              activeTab === "view" ? "active text-primary" : "text-dark"
            }`}
            onClick={handleTabView}
          >
            View My Pitches
          </button>
        </div>
        {activeTab === "create" ? (
          <>
            {disableInput() && (
              <>
                <div className="alert alert-primary" role="alert">
                  Pitch has been {formData.status.toLowerCase()} and cannot be
                  amended can only be viewed in read only mode.
                </div>
              </>
            )}

            {pitchStep === 0 ? (
              <form onSubmit={handleSubmit(submitPitch)}>
                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>Business Overview</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">Pitch Name</label>
                      <input
                        type="text"
                        {...register("name", {
                          required: "Name is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.name ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Pitch Name"
                      />
                      {pitchErrors.name && (
                        <div className="invalid-feedback">
                          {pitchErrors.name.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">Description</label>
                      <textarea
                        {...register("companyDescription", {
                          required: "Description is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.companyDescription ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Description"
                      />
                      {pitchErrors.companyDescription && (
                        <div className="invalid-feedback">
                          {pitchErrors.companyDescription.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">Mission</label>
                      <input
                        type="text"
                        {...register("mission", {
                          required: "Mission is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.mission ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Mission"
                      />
                      {pitchErrors.mission && (
                        <div className="invalid-feedback">
                          {pitchErrors.mission.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">Vision</label>
                      <input
                        type="text"
                        {...register("vision", {
                          required: "Vision is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.vision ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Vision"
                      />
                      {pitchErrors.vision && (
                        <div className="invalid-feedback">
                          {pitchErrors.vision.message}
                        </div>
                      )}
                    </div>

                    <div className="mt-3 mb-3">
                      <label className="mb-3">Sectors of Interest</label>
                      <Controller
                        name="sector"
                        control={control}
                        rules={{
                          required: "At least one sector must be selected",
                        }}
                        render={({ field }) => {
                          const arr = normalizeSectors(field.value);
                          return (
                            <div>
                              <Select
                                options={SECTOR_OPTIONS}
                                isMulti
                                isDisabled={disableInput()}
                                placeholder="Select sectors..."
                                value={arr.map((val) => ({
                                  value: val,
                                  label: val,
                                }))}
                                onChange={(selected) => {
                                  const values = (
                                    selected as {
                                      value: string;
                                      label: string;
                                    }[]
                                  ).map((opt) => opt.value);
                                  field.onChange(values);
                                }}
                                styles={{
                                  control: (base, state) => ({
                                    ...base,
                                    borderColor: pitchErrors.sector
                                      ? "#dc3545"
                                      : base.borderColor,
                                  }),
                                }}
                                classNamePrefix="react-select"
                              />
                              {/* Validation Message */}
                              {pitchErrors.sector && (
                                <div
                                  className="text-danger mt-1"
                                  style={VALIDATION_MESSAGE_STYLE}
                                >
                                  {pitchErrors.sector.message}
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="mb-3">Investment Type</label>
                      <input
                        type="text"
                        {...register("investmentType", {
                          required: "Investment Type is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.vision ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Investment Type"
                      />
                      {pitchErrors.investmentType && (
                        <div className="invalid-feedback">
                          {pitchErrors.investmentType.message}
                        </div>
                      )}
                    </div>

                    <div className="mt-3 mb-3">
                      <label className="mb-3">Investment Ticket Size</label>
                      <Controller
                        name="investmentTicketSize"
                        control={control}
                        rules={{
                          required:
                            "At least one investment ticket size must be selected",
                        }}
                        render={({ field }) => {
                          // Convert selected values to {value, label} format for React Select
                          const value = INVESTMENT_SIZES.filter((opt) =>
                            field.value?.includes(opt.value)
                          )
                            .map((opt) => ({
                              value: opt.value,
                              label: opt.label,
                            }));

                          return (
                            <div>
                              <Select
                                options={INVESTMENT_SIZES}
                                isMulti
                                isDisabled={disableInput()}
                                placeholder="Select Investment Ticket Sizes..."
                                value={value}
                                onChange={(selected) => {
                                  const values = (
                                    selected as {
                                      value: string;
                                      label: string;
                                    }[]
                                  ).map((opt) => opt.value);
                                  field.onChange(values);
                                }}
                                styles={{
                                  control: (base, state) => ({
                                    ...base,
                                    borderColor:
                                      pitchErrors.investmentTicketSize
                                        ? "#dc3545"
                                        : base.borderColor,
                                  }),
                                }}
                                classNamePrefix="react-select"
                              />
                              {pitchErrors.investmentTicketSize && (
                                <div
                                  className="text-danger mt-1"
                                  style={VALIDATION_MESSAGE_STYLE}
                                >
                                  {pitchErrors.investmentTicketSize.message}
                                </div>
                              )}
                            </div>
                          );
                        }}
                      />
                    </div>

                    <div className="mt-3 mb-3">
                      <label className="mb-3">Region</label>
                      <select
                        {...register("region", {
                          required: "Region is required",
                        })}
                        className={`form-select ${
                          pitchErrors.region ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                      >
                        <option value="">Please select</option>

                        {COUNTRY_OPTIONS.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                      {pitchErrors.region && (
                        <div className="invalid-feedback">
                          {pitchErrors.region.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>Problem Statement</h4>
                  <div className="card-body">
                    <label className="mb-3">
                      What problem have you identified in the market ?
                    </label>
                    <div className="mb-3">
                      <textarea
                        {...register("identifiedMarketProblem", {
                          required: "Description is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.identifiedMarketProblem
                            ? "is-invalid"
                            : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Identified Market Problem"
                      />
                      {pitchErrors.identifiedMarketProblem && (
                        <div className="invalid-feedback">
                          {pitchErrors.identifiedMarketProblem.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>Please provide details of your proposed solution</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">Solution</label>
                      <textarea
                        {...register("solution", {
                          required: "Solution is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.solution ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Solution"
                      />
                      {pitchErrors.solution && (
                        <div className="invalid-feedback">
                          {pitchErrors.solution.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>Market Opportunity</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">
                        What is the Market Size / TAN (GBP)
                      </label>
                      <select
                        {...register("marketSize", {
                          required: "Market Size is required",
                        })}
                        className={`form-select ${
                          pitchErrors.marketSize ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                      >
                        <option value="">Please select</option>
                        <option value="<1m">&lt;1m</option>
                        <option value="1m-5m">1m-5m</option>
                        <option value="5m-10m">5m-10m</option>
                        <option value="10m-50m">10m-50m</option>
                        <option value="50m-100m">50m-100m</option>
                        <option value="500m-1b">500m-1b</option>
                        <option value="1b+">1b+</option>
                      </select>
                      {pitchErrors.marketSize && (
                        <div className="invalid-feedback">
                          {pitchErrors.marketSize.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        What is the Growth Potential?
                      </label>
                      <textarea
                        {...register("growthPotential", {
                          required: "Growth Potential is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.growthPotential ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Growth Potential"
                      />
                      {pitchErrors.growthPotential && (
                        <div className="invalid-feedback">
                          {pitchErrors.growthPotential.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        Who is the Target Audience?
                      </label>
                      <textarea
                        {...register("targetAudience", {
                          required: "Target Audience is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.targetAudience ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Target Audience"
                      />
                      {pitchErrors.targetAudience && (
                        <div className="invalid-feedback">
                          {pitchErrors.targetAudience.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>Business Model</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">
                        What are the Revenue Streams?
                      </label>
                      <textarea
                        {...register("revenueStreams", {
                          required: "Revenue Stream is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.revenueStreams ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Revenue Stream"
                      />
                      {pitchErrors.revenueStreams && (
                        <div className="invalid-feedback">
                          {pitchErrors.revenueStreams.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        What is the Pricing Strategy?
                      </label>

                      <textarea
                        {...register("pricingStrategy", {
                          required: "Pricing Strategy is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.pricingStrategy ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Revenue Stream"
                      />
                      {pitchErrors.pricingStrategy && (
                        <div className="invalid-feedback">
                          {pitchErrors.pricingStrategy.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>Financial Projections</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">
                        What is the Revenue Forecast?
                      </label>

                      <textarea
                        {...register("revenueForecast", {
                          required: "Revenue Forecast is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.revenueForecast ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Revenue Forecast"
                      />
                      {pitchErrors.revenueForecast && (
                        <div className="invalid-feedback">
                          {pitchErrors.revenueForecast.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">
                        What are the Profit Margins?
                      </label>

                      <textarea
                        {...register("profitMargins", {
                          required: "Profit Margin is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.profitMargins ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Profit Margins"
                      />
                      {pitchErrors.profitMargins && (
                        <div className="invalid-feedback">
                          {pitchErrors.profitMargins.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">Break-Even Margins</label>

                      <textarea
                        {...register("breakEvenMargins", {
                          required: "Break-Even is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.breakEvenMargins ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Break-Even Margins"
                      />
                      {pitchErrors.breakEvenMargins && (
                        <div className="invalid-feedback">
                          {pitchErrors.breakEvenMargins.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card mt-3 shadow-sm border-0 p-2">
                  <h4>Funding Requirements</h4>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="mb-3">Amount Sought (in GBP)</label>

                      <input
                        type="number"
                        {...register("amountSought", {
                          required: "Amount Sought is required",
                          valueAsNumber: true,
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.amountSought ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter Amount Sought"
                      />
                      {pitchErrors.amountSought && (
                        <div className="invalid-feedback">
                          {pitchErrors.amountSought.message}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="mb-3">Use of Funds</label>

                      <textarea
                        {...register("useOfFunds", {
                          required: "Use of Funds is required",
                        })}
                        className={`form-control mb-2 ${
                          pitchErrors.useOfFunds ? "is-invalid" : ""
                        }`}
                        disabled={disableInput()}
                        placeholder="Enter use of funds"
                      />
                      {pitchErrors.useOfFunds && (
                        <div className="invalid-feedback">
                          {pitchErrors.useOfFunds.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {formData.status !== "Submitted" ? (
                  <div className="mt-3 d-flex justify-content-end">
                    <Button
                      disabled={isPending}
                      type="submit"
                      variant="primary"
                      size="sm"
                      className="shadow-lg border-0 rounded-pill me-2 d-flex align-items-center justify-content-center"
                    >
                      <BsFloppy className="me-1" /> Submit and Continue
                    </Button>
                  </div>
                ) : (
                  <></>
                )}
              </form>
            ) : (
              <Suspense fallback={null}>
                <UploadFiles
                  disableInput={disableInput}
                  formData={formData}
                  setFormData={setFormData}
                  companyProfile={companyProfile}
                />
              </Suspense>
            )}
          </>
        ) : activeTab === "view" ? (
          <div>
            <AllPitches
              pitchesData={pitchesList}
              onEdit={handleEditPitch}
              onDelete={handleDeletePitch}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}

const CreatePitchMemo = memo(CreatePitch);
CreatePitchMemo.displayName = "CreatePitch";
export default CreatePitchMemo;
